import { useMemo } from 'react';
import {
  Form,
  type FormItemProps,
  Skeleton,
  Space,
  Spin,
  Typography,
} from '@timeedit/ui-components/antd';
import type { DefaultOptionType } from '@timeedit/ui-components/antd/select/index';
import {
  Select,
  type TESelectProps,
} from '@timeedit/ui-components/components/InputComponent/Input';

export type MappingSelectProps = TESelectProps<number> & {
  name: FormItemProps['name'];
  label: FormItemProps['label'];
  tooltip?: FormItemProps['tooltip'];
  options: (DefaultOptionType & {
    extId?: string;
  })[];
};

export const MappingSelect = ({
  label,
  name,
  options,
  tooltip,
  ...props
}: MappingSelectProps) => {
  // Extend options with a search field that combines the label and the extId for better searchability
  const optionsToUse = useMemo(
    () =>
      options.map((option) => ({
        ...option,
        searchField: `${option.label}${option.extId ?? ''}`,
      })),
    [options]
  );

  const optionRender = (option: DefaultOptionType) => {
    return (
      <Space>
        {option.label}
        {option.data.extId ? (
          <Typography.Text code>{option.data.extId}</Typography.Text>
        ) : null}
      </Space>
    );
  };

  return (
    <Form.Item label={label} name={name} tooltip={tooltip}>
      {props.loading ? (
        <Skeleton
          active
          loading={props.loading}
          paragraph={{ rows: 1, width: '100%' }}
          title={false}
        />
      ) : (
        <Select
          showSearch
          optionFilterProp="searchField"
          notFoundContent={props.loading ? <Spin /> : undefined}
          optionRender={optionRender}
          {...props}
          disabled={props.loading || props.disabled}
          options={optionsToUse}
        />
      )}
    </Form.Item>
  );
};
